<script setup>
import AppLayout from '@/layouts/AppLayout.vue'

defineProps({
  rapport:        { type: Object, default: () => ({}) },
  estimation:     { type: Object, default: () => ({}) },
  projet:         { type: Object, default: () => ({}) },
  audit_entries:  { type: Array,  default: () => [] },
  can_see_audits: { type: Boolean, default: false },
})

const thCls = 'px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline'
const tdCls = 'px-4 py-3 text-sm text-on-surface'
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="rapport.url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <a :href="projet.url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-colors">{{ projet.code }}</a>
        <span class="text-outline">/</span>
        <a :href="estimation.url" class="font-mono font-bold text-sm text-primary hover:brightness-125 transition-colors">{{ estimation.code }}</a>
        <span class="text-outline">/</span>
        <a :href="rapport.url" class="font-mono font-bold text-sm text-primary hover:brightness-125 transition-colors">{{ rapport.date }}</a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Audit</span>
      </div>
    </template>

    <div class="p-8 space-y-6">

      <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">Historique d'audit</h1>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

        <!-- ── Détails du bordereau ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Détails du bordereau</h2>
          </div>
          <div class="divide-y divide-outline-variant/10">
            <div class="px-6 py-3 flex items-start justify-between gap-4">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline shrink-0">Projet</span>
              <a :href="projet.url" class="font-mono font-bold text-primary hover:brightness-125 transition-colors text-sm text-right">{{ projet.code }}</a>
            </div>
            <div class="px-6 py-3 flex items-start justify-between gap-4">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline shrink-0">Estimation</span>
              <div class="text-right">
                <a :href="estimation.url" class="font-mono font-bold text-primary hover:brightness-125 transition-colors text-sm">{{ estimation.code }}</a>
                <span class="block text-[10px] text-outline/60">{{ estimation.rapports_count }} rapport(s)</span>
              </div>
            </div>
            <div class="px-6 py-3 flex items-start justify-between gap-4">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline shrink-0">Date</span>
              <span class="font-mono font-bold text-on-surface text-sm">{{ rapport.date }}</span>
            </div>
            <div v-if="rapport.has_chainage" class="px-6 py-3 flex items-start justify-between gap-4">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline shrink-0">{{ rapport.chainage_label }}</span>
              <span class="font-mono text-sm text-on-surface text-right">
                de {{ rapport.chainage_debut }} à {{ rapport.chainage_fin }}
              </span>
            </div>
            <div class="px-6 py-3 flex items-start justify-between gap-4">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline shrink-0">Contremaître</span>
              <span class="text-sm text-on-surface text-right">{{ rapport.utilisateur_nom }}</span>
            </div>
          </div>
          <!-- Actions -->
          <div class="px-6 py-4 border-t border-outline-variant/10 flex gap-2">
            <a :href="rapport.url"
               class="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface border border-outline-variant/20 rounded-md transition-all">
              <span class="material-symbols-outlined text-sm">visibility</span>Voir
            </a>
            <a :href="rapport.edit_url"
               class="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface border border-outline-variant/20 rounded-md transition-all">
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
        </div>

        <!-- ── Historique d'audit ── -->
        <div class="lg:col-span-2 bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Historique des modifications</h2>
            <span class="text-[10px] font-black text-outline/40 uppercase tracking-widest">
              {{ audit_entries.length }} entrée{{ audit_entries.length > 1 ? 's' : '' }}
            </span>
          </div>

          <div v-if="!can_see_audits" class="px-6 py-12 text-center text-outline/40 text-sm italic">
            Vous n'avez pas accès à l'historique d'audit.
          </div>

          <div v-else-if="!audit_entries.length" class="px-6 py-12 text-center text-outline/40 text-sm italic">
            Aucune entrée d'audit pour ce bordereau.
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th :class="thCls">Utilisateur</th>
                  <th :class="thCls">Action</th>
                  <th :class="thCls">Date</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(entry, i) in audit_entries" :key="i"
                    class="hover:bg-surface-container-highest/20 transition-colors">
                  <td :class="tdCls + ' font-bold'">{{ entry.utilisateur }}</td>
                  <td :class="tdCls">
                    <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-surface-container-high text-on-surface/80 text-xs font-bold">
                      {{ entry.action }}
                    </span>
                  </td>
                  <td :class="tdCls + ' font-mono text-outline'">{{ entry.action_date }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>
